/*
Custom hook for saving and loading vehicle publication data
Handles ChileAutos, MercadoLibre, and Yapo draft data
Author: Assistant
Date: 2025-01-24
*/

import { useState } from 'react'

import { useAuth } from './useAuth'
import { makeUrlSavePublicationData, makeUrlLoadPublicationData } from './connectionData'

type PublicationPlatform = 'chileautos' | 'meli' | 'yapo'

interface SaveResponse {
  success: boolean
  message?: string
  savedAt?: string
}

interface LoadResponse<T> {
  success: boolean
  data?: T
  message?: string
}

const usePublicationDataSaver = () => {
  const [saveLoading, setSaveLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const { authToken } = useAuth()

  const savePublicationData = async <T extends Record<string, unknown>>(
    vehicleId: string,
    platform: PublicationPlatform,
    data: T
  ): Promise<SaveResponse> => {
    setSaveLoading(true)
    setError(null)

    try {
      if (!authToken) {
        throw new Error('Usuario no autenticado')
      }

      const apiUrl = makeUrlSavePublicationData(vehicleId, platform)
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        // Handle different error types
        if (response.status === 401) {
          throw new Error('Usuario no autenticado. Por favor, inicie sesión nuevamente.')
        }

        if (response.status === 403) {
          throw new Error('Acceso denegado. No tiene permisos para esta acción.')
        }

        // Try to get JSON error message, fallback to status text
        try {
          const errorData = await response.json()
          throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`)
        } catch {
          throw new Error(`Error ${response.status}: ${response.statusText}`)
        }
      }

      const result = await response.json()

      return {
        success: true,
        message: result.message,
        savedAt: result.savedAt,
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido'
      setError(errorMessage)

      return {
        success: false,
        message: errorMessage,
      }
    } finally {
      setSaveLoading(false)
    }
  }

  const loadPublicationData = async <T = unknown,>(
    vehicleId: string,
    platform: PublicationPlatform
  ): Promise<LoadResponse<T>> => {
    setError(null)

    try {
      if (!authToken) {
        throw new Error('Usuario no autenticado')
      }

      const apiUrl = makeUrlLoadPublicationData(vehicleId, platform)
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })

      if (!response.ok) {
        if (response.status === 404) {
          return {
            success: true,
            data: undefined,
            message: 'No hay datos guardados',
          }
        }

        // Handle different error types
        if (response.status === 401) {
          throw new Error('Usuario no autenticado. Por favor, inicie sesión nuevamente.')
        }

        if (response.status === 403) {
          throw new Error('Acceso denegado. No tiene permisos para esta acción.')
        }

        // Try to get JSON error message, fallback to status text
        try {
          const errorData = await response.json()
          throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`)
        } catch {
          throw new Error(`Error ${response.status}: ${response.statusText}`)
        }
      }

      const result = await response.json()

      return {
        success: true,
        data: result as T, // Backend returns data directly, not wrapped in publish_data
        message: 'Datos cargados correctamente',
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido'
      setError(errorMessage)

      return {
        success: false,
        message: errorMessage,
      }
    } finally {
      // Load operations don't need loading state tracking
    }
  }

  const clearError = () => setError(null)

  return {
    savePublicationData,
    loadPublicationData,
    loading: saveLoading,
    error,
    clearError,
  }
}

export default usePublicationDataSaver
